import { supabase } from './supabase';
import { AccountingCategory, CashflowMapping, ApiResponse } from '../types/database';

export interface CreateAccountingCategoryData {
  company_id: string;
  name: string;
  cashflow_mapping: CashflowMapping;
}

export interface UpdateAccountingCategoryData {
  name?: string;
  cashflow_mapping?: CashflowMapping;
  is_active?: boolean;
}

/**
 * Get all accounting categories for a company
 */
export async function getAccountingCategories(
  companyId: string
): Promise<ApiResponse<AccountingCategory[]>> {
  try {
    const { data, error } = await supabase
      .from('accounting_categories')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching accounting categories:', error);
      return {
        data: null,
        error: { message: error.message, code: error.code, details: error },
        status: 400,
      };
    }

    return {
      data: data || [],
      error: null,
      status: 200,
    };
  } catch (error: unknown) {
    console.error('Unexpected error fetching accounting categories:', error);
    return {
      data: null,
      error: { message: 'An unexpected error occurred', details: error },
      status: 500,
    };
  }
}

/**
 * Get a single accounting category by ID
 */
export async function getAccountingCategory(
  categoryId: string
): Promise<ApiResponse<AccountingCategory>> {
  try {
    const { data, error } = await supabase
      .from('accounting_categories')
      .select('*')
      .eq('id', categoryId)
      .single();

    if (error) {
      console.error('Error fetching accounting category:', error);
      return {
        data: null,
        error: { message: error.message, code: error.code, details: error },
        status: 400,
      };
    }

    return {
      data,
      error: null,
      status: 200,
    };
  } catch (error: unknown) {
    console.error('Unexpected error fetching accounting category:', error);
    return {
      data: null,
      error: { message: 'An unexpected error occurred', details: error },
      status: 500,
    };
  }
}

/**
 * Create a new accounting category
 */
export async function createAccountingCategory(
  categoryData: CreateAccountingCategoryData
): Promise<ApiResponse<AccountingCategory>> {
  try {
    const { data, error } = await supabase
      .from('accounting_categories')
      .insert([categoryData])
      .select()
      .single();

    if (error) {
      console.error('Error creating accounting category:', error);
      return {
        data: null,
        error: { message: error.message, code: error.code, details: error },
        status: 400,
      };
    }

    return {
      data,
      error: null,
      status: 201,
    };
  } catch (error: unknown) {
    console.error('Unexpected error creating accounting category:', error);
    return {
      data: null,
      error: { message: 'An unexpected error occurred', details: error },
      status: 500,
    };
  }
}

/**
 * Update an accounting category
 */
export async function updateAccountingCategory(
  categoryId: string,
  updates: UpdateAccountingCategoryData
): Promise<ApiResponse<AccountingCategory>> {
  try {
    const { data, error } = await supabase
      .from('accounting_categories')
      .update(updates)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating accounting category:', error);
      return {
        data: null,
        error: { message: error.message, code: error.code, details: error },
        status: 400,
      };
    }

    return {
      data,
      error: null,
      status: 200,
    };
  } catch (error: unknown) {
    console.error('Unexpected error updating accounting category:', error);
    return {
      data: null,
      error: { message: 'An unexpected error occurred', details: error },
      status: 500,
    };
  }
}

/**
 * Soft delete an accounting category (set is_active to false)
 */
export async function deleteAccountingCategory(
  categoryId: string
): Promise<ApiResponse<null>> {
  try {
    const { error } = await supabase
      .from('accounting_categories')
      .update({ is_active: false })
      .eq('id', categoryId);

    if (error) {
      console.error('Error deleting accounting category:', error);
      return {
        data: null,
        error: { message: error.message, code: error.code, details: error },
        status: 400,
      };
    }

    return {
      data: null,
      error: null,
      status: 200,
    };
  } catch (error: unknown) {
    console.error('Unexpected error deleting accounting category:', error);
    return {
      data: null,
      error: { message: 'An unexpected error occurred', details: error },
      status: 500,
    };
  }
}

export const accountingCategoriesService = {
  getAccountingCategories,
  getAccountingCategory,
  createAccountingCategory,
  updateAccountingCategory,
  deleteAccountingCategory,
};
